import type { Pt } from './geometry'
import type { MaskCrop } from './maskCrop'
import { MESSAGES } from './messages'
import type { Stroke } from './strokeGesture'

export type MaskSlicePlan = { keep: Uint8Array; piece: Uint8Array }
type Result = MaskSlicePlan | { error: string }

const neighbours = (i: number, width: number, size: number): number[] => {
  const x = i % width
  const result: number[] = []
  if (x > 0) result.push(i - 1)
  if (x < width - 1) result.push(i + 1)
  if (i - width >= 0) result.push(i - width)
  if (i + width < size) result.push(i + width)
  return result
}

export const labelComponents = (
  data: Uint8Array,
  width: number,
): { labels: Int32Array; sizes: number[] } => {
  const labels = new Int32Array(data.length)
  const sizes = [0]
  const stack = new Int32Array(data.length)
  for (let start = 0; start < data.length; start++) {
    if (!data[start] || labels[start]) {
      continue
    }
    const label = sizes.length
    sizes.push(0)
    let top = 0
    stack[top++] = start
    labels[start] = label
    const visit = (j: number): void => {
      if (data[j] && !labels[j]) {
        labels[j] = label
        stack[top++] = j
      }
    }
    while (top) {
      const i = stack[--top]
      sizes[label]++
      const x = i % width
      if (x > 0) visit(i - 1)
      if (x < width - 1) visit(i + 1)
      if (i >= width) visit(i - width)
      if (i + width < data.length) visit(i + width)
    }
  }
  return { labels, sizes }
}

const countOf = (mask: Uint8Array): number => mask.reduce((sum, value) => sum + value, 0)

const paintedAt = (crop: MaskCrop, p: Pt): boolean => {
  const x = Math.floor(p.x) - crop.originX
  const y = Math.floor(p.y) - crop.originY
  return (
    x >= 0 && y >= 0 && x < crop.width && y < crop.height && crop.data[y * crop.width + x] === 1
  )
}

// Grid traversal (Amanatides-Woo): consecutive cells share an edge, so the cut cannot leak diagonally.
const markSegment = (barrier: Uint8Array, crop: MaskCrop, a: Pt, b: Pt): void => {
  const x0 = a.x - crop.originX
  const y0 = a.y - crop.originY
  const x1 = b.x - crop.originX
  const y1 = b.y - crop.originY
  let cx = Math.floor(x0)
  let cy = Math.floor(y0)
  const ex = Math.floor(x1)
  const ey = Math.floor(y1)
  const dx = x1 - x0
  const dy = y1 - y0
  const sx = Math.sign(dx)
  const sy = Math.sign(dy)
  const tDeltaX = dx ? Math.abs(1 / dx) : Infinity
  const tDeltaY = dy ? Math.abs(1 / dy) : Infinity
  let tMaxX = dx > 0 ? (cx + 1 - x0) * tDeltaX : dx < 0 ? (x0 - cx) * tDeltaX : Infinity
  let tMaxY = dy > 0 ? (cy + 1 - y0) * tDeltaY : dy < 0 ? (y0 - cy) * tDeltaY : Infinity
  const mark = (): void => {
    if (cx >= 0 && cy >= 0 && cx < crop.width && cy < crop.height) {
      barrier[cy * crop.width + cx] = 1
    }
  }
  mark()
  let steps = Math.abs(ex - cx) + Math.abs(ey - cy)
  while (steps-- > 0) {
    // an axis that has reached its end cell never steps again, even on a tie
    const stepX = cy === ey || (cx !== ex && tMaxX < tMaxY)
    if (stepX) {
      tMaxX += tDeltaX
      cx += sx
    } else {
      tMaxY += tDeltaY
      cy += sy
    }
    mark()
  }
}

// Pixels whose centre is inside the lasso, filled row by row.
const lassoPixels = (crop: MaskCrop, ring: Pt[]): Uint8Array => {
  const inside = new Uint8Array(crop.width * crop.height)
  for (let y = 0; y < crop.height; y++) {
    const centreY = crop.originY + y + 0.5
    const xs: number[] = []
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const a = ring[i]
      const b = ring[j]
      if (a.y > centreY !== b.y > centreY) {
        xs.push(a.x + ((centreY - a.y) * (b.x - a.x)) / (b.y - a.y))
      }
    }
    xs.sort((p, q) => p - q)
    for (let k = 0; k + 1 < xs.length; k += 2) {
      const from = Math.max(0, Math.ceil(xs[k] - crop.originX - 0.5))
      const to = Math.min(crop.width - 1, Math.ceil(xs[k + 1] - crop.originX - 0.5) - 1)
      for (let x = from; x <= to; x++) {
        inside[y * crop.width + x] = 1
      }
    }
  }
  return inside
}

const maskOf = (labels: Int32Array, label: number): Uint8Array => {
  const mask = new Uint8Array(labels.length)
  for (let i = 0; i < labels.length; i++) {
    if (labels[i] === label) mask[i] = 1
  }
  return mask
}

// Line pixels go to the nearest piece, growing from both at once one distance step at a time;
// within a step, ties go to the keeper. Each pixel is visited a bounded number of times.
const assignBarrier = (
  labels: Int32Array,
  pending: number[],
  width: number,
  keeper: number,
  other: number,
): void => {
  const isPending = new Uint8Array(labels.length)
  for (const i of pending) {
    isPending[i] = 1
  }
  const queued = new Uint8Array(labels.length)
  let frontier = pending.filter((i) => neighbours(i, width, labels.length).some((j) => labels[j]))
  for (const i of frontier) {
    queued[i] = 1
  }
  while (frontier.length) {
    const claims: [number, number][] = []
    for (const i of frontier) {
      const around = neighbours(i, width, labels.length).map((j) => labels[j])
      const label = around.includes(keeper) ? keeper : around.includes(other) ? other : 0
      if (label) {
        claims.push([i, label])
      }
    }
    for (const [i, label] of claims) {
      labels[i] = label
      isPending[i] = 0
    }
    const next: number[] = []
    for (const [i] of claims) {
      for (const j of neighbours(i, width, labels.length)) {
        if (isPending[j] && !queued[j]) {
          queued[j] = 1
          next.push(j)
        }
      }
    }
    frontier = next
  }
}

const planLine = (crop: MaskCrop, line: Pt[], parts: number): Result => {
  const { width, data } = crop
  if (parts > 1) {
    return { error: MESSAGES.multiPartLine }
  }
  if (paintedAt(crop, line[0]) || paintedAt(crop, line[line.length - 1])) {
    return { error: MESSAGES.endsInside }
  }

  const barrier = new Uint8Array(data.length)
  for (let i = 0; i < line.length - 1; i++) {
    markSegment(barrier, crop, line[i], line[i + 1])
  }
  const work = new Uint8Array(data.length)
  const pending: number[] = []
  for (let i = 0; i < data.length; i++) {
    if (!data[i]) continue
    if (barrier[i]) pending.push(i)
    else work[i] = 1
  }
  const { labels, sizes } = labelComponents(work, width)
  const pieces = sizes.length - 1
  if (pieces < 2) {
    return { error: MESSAGES.noDivide }
  }
  if (pieces > 2) {
    return { error: MESSAGES.cutsMany(pieces) }
  }

  const split = (keeper: number): MaskSlicePlan => {
    const other = keeper === 1 ? 2 : 1
    const assigned = labels.slice()
    assignBarrier(assigned, pending, width, keeper, other)
    return { keep: maskOf(assigned, keeper), piece: maskOf(assigned, other) }
  }
  // the ID goes to the larger piece after line pixels are assigned, with ties going to that piece
  const first = split(sizes[1] >= sizes[2] ? 1 : 2)
  if (countOf(first.keep) >= countOf(first.piece)) {
    return first
  }
  const second = split(sizes[1] >= sizes[2] ? 2 : 1)
  return countOf(second.keep) >= countOf(second.piece)
    ? second
    : { keep: second.piece, piece: second.keep }
}

const planIslandLasso = (
  inside: Uint8Array,
  outside: Uint8Array,
  parts: { labels: Int32Array; sizes: number[] },
): Result => {
  const insidePerPart = new Array<number>(parts.sizes.length).fill(0)
  for (let i = 0; i < inside.length; i++) {
    if (inside[i]) insidePerPart[parts.labels[i]]++
  }
  const touched = insidePerPart.map((count, label) => (label ? count : 0))
  const partial = touched.some((count, label) => count > 0 && count < parts.sizes[label])
  const enclosed = touched.filter((count, label) => count > 0 && count === parts.sizes[label])
  if (partial || enclosed.length !== 1) {
    return { error: MESSAGES.encloseOne }
  }
  return { keep: outside, piece: inside }
}

const planLasso = (
  crop: MaskCrop,
  ring: Pt[],
  parts: { labels: Int32Array; sizes: number[] },
): Result => {
  const { width, data } = crop
  const lasso = lassoPixels(crop, ring)
  const inside = new Uint8Array(data.length)
  const outside = new Uint8Array(data.length)
  for (let i = 0; i < data.length; i++) {
    if (!data[i]) continue
    if (lasso[i]) inside[i] = 1
    else outside[i] = 1
  }
  if (!countOf(inside)) {
    return { error: MESSAGES.lassoNoTouch }
  }
  if (!countOf(outside)) {
    return { error: MESSAGES.coversAll }
  }
  if (parts.sizes.length - 1 > 1) {
    return planIslandLasso(inside, outside, parts)
  }
  const insideParts = labelComponents(inside, width).sizes.length - 1
  const outsideParts = labelComponents(outside, width).sizes.length - 1
  if (insideParts !== 1 || outsideParts !== 1) {
    return { error: MESSAGES.separatePieces }
  }
  return { keep: outside, piece: inside }
}

export const planMaskSlice = (crop: MaskCrop, stroke: Stroke): Result => {
  const parts = labelComponents(crop.data, crop.width)
  const partCount = parts.sizes.length - 1
  if (!partCount) {
    return { error: stroke.kind === 'line' ? MESSAGES.noDivide : MESSAGES.lassoNoTouch }
  }
  return stroke.kind === 'line'
    ? planLine(crop, stroke.points, partCount)
    : planLasso(crop, stroke.points, parts)
}
